import { useState } from "react";
import { toast } from "sonner";
import { Check, PencilLine, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { errMsg } from "@/lib/api";
import { formatoLabel } from "@/lib/ped";
import { portalApi, mediaUrl } from "@/lib/portal";
import { ChannelIcons } from "@/components/ped/PedBits";

const Media = ({ token, post }) => (
  <div className="grid grid-cols-3 gap-2 mt-4">
    {post.media.map((m) => m.content_type.startsWith("video/")
      ? <video key={m.id} src={mediaUrl(token, post.id, m.id)} controls className="w-full aspect-square object-cover rounded-xl bg-[#0A1128]" data-testid={`portal-media-${m.id}`} />
      : <img key={m.id} src={mediaUrl(token, post.id, m.id)} alt={m.nome} className="w-full aspect-square object-cover rounded-xl" data-testid={`portal-media-${m.id}`} />)}
  </div>
);

const ChangesDialog = ({ open, onOpenChange, onSubmit, busy }) => {
  const [txt, setTxt] = useState("");
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg rounded-2xl" data-testid="portal-changes-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl font-black text-[#0A1128]">Richiedi modifiche</DialogTitle>
          <DialogDescription>Spiegaci cosa vorresti cambiare: testo, immagini, data o altro.</DialogDescription>
        </DialogHeader>
        <Textarea rows={5} value={txt} onChange={(e) => setTxt(e.target.value)} placeholder="Il tuo commento (obbligatorio)" className="rounded-xl" data-testid="portal-changes-comment" />
        <DialogFooter>
          <Button variant="ghost" className="rounded-full" onClick={() => onOpenChange(false)} data-testid="portal-changes-cancel">Annulla</Button>
          <Button disabled={busy || !txt.trim()} onClick={() => onSubmit(txt)} className="rounded-full bg-[#E8173A] hover:bg-[#C8102E] font-bold" data-testid="portal-changes-submit">{busy && <Loader2 className="h-4 w-4 animate-spin mr-1.5" />}Invia richiesta</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export const PortalPostCard = ({ post: p, token, onChange, readOnly }) => {
  const [busy, setBusy] = useState(false);
  const [changes, setChanges] = useState(false);
  const act = async (path, body, ok) => {
    setBusy(true);
    try { const r = await portalApi.post(`/${token}/posts/${p.id}/${path}`, body); toast.success(ok); setChanges(false); onChange?.(r.data); }
    catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(false); }
  };
  return (
    <article className={`rounded-2xl border border-[#5FB3EA] bg-[#8FD0FB] p-6 shadow-[0_2px_10px_rgba(10,17,40,0.08)] ${readOnly ? "opacity-80" : ""}`} data-testid={`portal-post-${p.id}`}>
      <div className="flex items-center justify-between gap-4">
        <div className="text-sm font-bold text-[#0A1128] first-letter:uppercase">{new Date(p.data_pubblicazione).toLocaleString("it-IT", { timeZone: "Europe/Rome", weekday: "long", day: "numeric", month: "long", year: "numeric", hour: "2-digit", minute: "2-digit" })}</div>
        <div className="flex items-center gap-3"><ChannelIcons canali={p.canali} size={18} /><span className="text-xs font-bold uppercase tracking-wider rounded-full bg-[#0A1128] text-white px-2.5 py-0.5">{formatoLabel(p.formato)}</span></div>
      </div>
      <h3 className="font-display text-xl font-black text-[#0A1128] mt-3">{p.titolo}</h3>
      {p.testo && <p className="text-[#0A1128] whitespace-pre-wrap mt-2" data-testid={`portal-post-caption-${p.id}`}>{p.testo}</p>}
      {p.hashtag && <p className="text-sm font-semibold text-[#0B4F8A] mt-2">{p.hashtag}</p>}
      {p.media.length > 0 && <Media token={token} post={p} />}
      {readOnly ? <div className="mt-4 inline-flex items-center gap-1.5 text-sm font-bold text-emerald-800" data-testid={`portal-post-approved-${p.id}`}><Check className="h-4 w-4" />Approvato</div> : (
        <div className="flex gap-3 mt-5">
          <Button disabled={busy} onClick={() => act("approva", {}, "Contenuto approvato. Grazie!")} className="rounded-full bg-[#0A1128] hover:bg-emerald-700 font-bold px-6" data-testid={`portal-approve-${p.id}`}><Check className="h-4 w-4 mr-1.5" />Approva</Button>
          <Button disabled={busy} variant="outline" onClick={() => setChanges(true)} className="rounded-full border-[#E8173A] text-[#E8173A] hover:bg-red-50 font-bold px-6" data-testid={`portal-request-changes-${p.id}`}><PencilLine className="h-4 w-4 mr-1.5" />Richiedi modifiche</Button>
        </div>
      )}
      <ChangesDialog open={changes} onOpenChange={setChanges} busy={busy} onSubmit={(c) => act("modifiche", { commento: c }, "Richiesta inviata all'agenzia")} />
    </article>
  );
};
